import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { PerformanceTabSectionList } from "features/Library/Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";
import { ListViewBase } from "../../../../components/listView";
import { type PerformanceFilter, noData, noTableData } from "features/Library/Common/utils/performanceUtils";
import { useEffect, useRef, useCallback, useState } from "react";
import type SortDirections from "enums/sortDirections";
import { buildTableBody, createSelectionChangeHandler } from "./genericPerformanceListUtils";

export interface Props<T extends object> {
  rows: T[];
  columns: ColumnOption<T>[];
  filter: PerformanceFilter;
  title?: string;
  hideControls?: boolean;
  onSort?: (sortingColumnName: string, sortingDirection: SortDirections) => void;
  withSelection?: boolean;
  onSelectionChanged?: (data: T[]) => void;
}

type ReloadItemsCallback = (condition?: boolean) => void;

export const GenericPerformanceList = <T extends object>({
  rows,
  columns,
  filter,
  hideControls,
  title = "",
  onSort,
  withSelection = false,
  onSelectionChanged,
}: Props<T>) => {
  const [slicedEntities, setSliceEntities] = useState<Array<T>>([]);
  const reloadItemsRef = useRef<ReloadItemsCallback>();
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const buildTableBodyCells = useCallback((item: T) => buildTableBody(item, columns), [columns]);

  const getData = useCallback(
    (skip: number, top: number, sortingColumnName: string, sortingDirection: SortDirections) => {
      setSliceEntities(rows.slice(skip, skip + top));
      if (sortingColumnName && sortingDirection) {
        onSort?.(sortingColumnName, sortingDirection);
      }
    },
    [onSort, rows],
  );
  const reloadItems = (reloadListItems: ReloadItemsCallback) => (reloadItemsRef.current = reloadListItems);

  // Refresh the data list on rows change
  useEffect(() => {
    reloadItemsRef.current?.();
  }, [rows]);

  const handleSelectionChanged = createSelectionChangeHandler(setSelectedItems);

  useEffect(() => {
    onSelectionChanged?.(selectedItems);
  }, [onSelectionChanged, selectedItems]);

  return (
    <PerformanceTabSectionList
      hideControls={hideControls}
      title={title}
      count={rows.length}
      renderList={(page) => {
        return noTableData(rows) ? (
          noData(filter)
        ) : (
          <ListViewBase
            columnOptions={columns}
            loadPage={getData}
            items={slicedEntities}
            itemsAmount={rows.length}
            buildTableBody={buildTableBodyCells}
            withFooter={false}
            activePage={page}
            withSelection={withSelection}
            onSelectionChanged={handleSelectionChanged}
            setReloadItems={reloadItems}
            // Preserve pagination when sorting
            paginateOnLoad
          />
        );
      }}
    />
  );
};
